import { brokerOrgJsonLd } from "@/lib/seo-schema";
import { PageDatesBadge } from "@/components/site/PageDatesBadge";
import { CountyInsightsPanel } from "@/components/site/CountyInsightsPanel";
import { LocalMarketNews } from "@/components/site/LocalMarketNews";
import { getCountyInsights } from "@/lib/county-insights.functions";
import { RenterDemandPanel } from "@/components/site/RenterDemandPanel";
import { getState as getStateForCounty } from "@/data/states";
import { absoluteUrl } from "@/lib/site-origin";
import { ExpertInsight } from "@/components/site/SeoBlocks";
import { buildCityExpertInsight } from "@/data/expert-insights";
import { states as ALL_STATES } from "@/data/states";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { QuickAnswer, PageOutro } from "@/components/site/PageEssentials";
import {
  Section,
  SectionHeader,
  FAQ,
  Breadcrumbs,
  CTABand,
  StatRow,
  TableOfContents,
  LoanGuidelines,
} from "@/components/site/Blocks";
import { RentalIncomeCompare } from "@/components/site/RentalIncomeCompare";
import { InvestmentRationale } from "@/components/site/InvestmentRationale";
import { DataSources } from "@/components/site/DataSources";
import { MarketUpdateSection } from "@/components/site/MarketUpdateSection";
import { CityGuideDownload } from "@/components/site/CityGuideDownload";
import { LandlordLawPanel } from "@/components/site/LandlordLawPanel";
import { PhaDirectoryPanel } from "@/components/site/PhaDirectoryPanel";
import { VoucherDemandPanel } from "@/components/site/VoucherDemandPanel";
import { Section8Calculators } from "@/components/site/Section8Calculators";
import { HapTimelinePanel } from "@/components/site/HapTimelinePanel";
import { hapTimelineHowToJsonLd } from "@/components/site/HapTimelinePanel.seo";
import { HqsChecklistDownload } from "@/components/site/HqsChecklistDownload";
import { Section8Resources } from "@/components/site/Section8Resources";
import { Section8CaseStudy } from "@/components/site/Section8CaseStudy";
import { SisterProductLinks } from "@/components/site/SisterProductLinks";
import { VerifiedStamp } from "@/components/site/VerifiedStamp";
import { getProductHero } from "@/data/productHeroes";
import { getCityHero } from "@/data/city-heroes";
import { getState } from "@/data/states";
import { findCity, publishedInvestorCities, type InvestorCity } from "@/data/investor-cities";
import { requiresEntity } from "@/data/licensed-states";
import type { Section8CityContext } from "@/data/section8-city-context";
import { fetchSection8CityContext } from "@/lib/section8-city-context.functions";
import { getMarketStats, type MarketStats } from "@/lib/market-stats.functions";
import { getFmrByZip } from "@/lib/fmr.functions";
import {
  localBusinessJsonLd,
  cityArticleJsonLd,
  faqJsonLd,
  breadcrumbListJsonLd,
  serviceJsonLd,
} from "@/lib/seo-schema";
import { cityKeywordsMeta } from "@/lib/page-keywords";
import { makeReportInlineCTA } from "@/components/site/ReportCta";

const InlineCTA = makeReportInlineCTA("section-8");

const SLUG = "section-8-loans";

const searchSchema = z.object({
  income: fallback(z.string().optional(), undefined).optional(),
});

function fmtCurrency(n: number | null | undefined): string | null {
  if (n == null) return null;
  return `$${Intl.NumberFormat("en-US").format(Math.round(n))}`;
}
function fmtPopulation(n: number | null | undefined): string | null {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return Intl.NumberFormat("en-US").format(n);
}
function withLiveStats(
  city: InvestorCity,
  stats: MarketStats | null,
): InvestorCity & { liveVintage?: string | null } {
  if (!stats) return city;
  return {
    ...city,
    medianHomeValue: fmtCurrency(stats.medianHomeValue) ?? city.medianHomeValue,
    population: fmtPopulation(stats.population) ?? city.population,
    liveVintage: stats.vintage,
  };
}

export const Route = createFileRoute("/section-8-loans/$state/$city")({
  validateSearch: zodValidator(searchSchema),
  loader: async ({ params }) => {
    const rawCity = findCity(params.state, params.city);
    if (!rawCity) throw notFound();
    const countyInsights = await getCountyInsights({
      data: {
        stateAbbrev: getStateForCounty(params.state)?.abbrev ?? "",
        countyName: rawCity.county,
        placeLabel: `${rawCity.name}, ${rawCity.stateName}`,
      },
    }).catch(() => null);
    const stats = await getMarketStats({
      data: { stateSlug: params.state, citySlug: params.city },
    }).catch(() => null);
    const city = withLiveStats(rawCity, stats);
    const s8 = await fetchSection8CityContext({ data: { state: params.state, city: params.city } }).catch(() => null);
    // Real published FMR for this city's centroid ZIP (Small Area FMR where HUD
    // publishes it, otherwise the county/metro FMR) so the Section 8 calculators
    // open with the number the PHA actually uses instead of a placeholder.
    const fmrLookup = city.centroidZip
      ? await getFmrByZip({ data: { zip: city.centroidZip } }).catch(() => null)
      : null;
    const fmr =
      fmrLookup && fmrLookup.rents.twoBr > 0
        ? {
            value: fmrLookup.rents.twoBr,
            year: fmrLookup.year,
            smallArea: fmrLookup.smallArea,
            areaName: fmrLookup.metroName ?? fmrLookup.county ?? null,
            zip: city.centroidZip,
          }
        : null;
    return {
      s8,
      countyInsights,
      marketStats: stats,
      city,
      fmr,
      liveVintage: stats?.vintage ?? null,
      liveMedianRent: stats?.medianRent ?? null,
      liveMedianRent2BR: stats?.medianRent2BR ?? null,
      liveMedianRent3BR: stats?.medianRent3BR ?? null,
      liveUnemploymentRate: stats?.unemploymentRate ?? null,
      liveUnemploymentPeriod: stats?.unemploymentPeriod ?? null,
    };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "City not found" }, { name: "robots", content: "noindex" }] };
    }
    const c = loaderData.city;
    const s8 = loaderData.s8 ?? undefined;
    const phaTag = s8 ? ` | ${s8.primaryPha.split("+")[0].trim()}` : " | HUD FMR & HAP-Backed DSCR";
    const title = `Section 8 DSCR Loans in ${c.name}, ${c.stateName}${phaTag}`.slice(0, 66);
    const descBase = s8
      ? `Section 8 investor loans in ${c.name}, ${c.stateName}: ${s8.primaryPha} Payment Standards ${s8.paymentStandardBand.toLowerCase()}. DSCR financing on HAP contract rent. NMLS #2620881.`
      : `Section 8 rental property loans in ${c.name}, ${c.stateName}: qualify with HAP contract rent, HUD FMR benchmarks, and local Payment Standard. DSCR-based investor financing, NMLS #2620881.`;
    const desc = descBase.slice(0, 158);
    const url = `/${SLUG}/${params.state}/${params.city}`;
    const faqs = mergedFaqs(c.name, c.stateName, s8);
    const hero = getCityHero(params.state, params.city, c.name, c.stateName);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "keywords", content: cityKeywordsMeta(SLUG, c.name, c.stateName) },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://dscrloanmarket.com${url}` },
        ...(hero
          ? [
              { property: "og:image", content: absoluteUrl(hero.src) },
              { property: "og:image:alt", content: hero.alt },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(hero ? [{ name: "twitter:image", content: absoluteUrl(hero.src) }] : []),
        { property: "article:published_time", content: "2026-01-01" },
      ],
      links: [
        { rel: "canonical", href: `https://dscrloanmarket.com${url}` },
        { rel: "alternate", hrefLang: "en-us", href: `https://dscrloanmarket.com${url}` },
        { rel: "alternate", hrefLang: "x-default", href: `https://dscrloanmarket.com${url}` },
        ...(hero ? [{ rel: "preload", as: "image", href: hero.src }] : []),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(serviceJsonLd({ url, serviceName: `Section 8 DSCR Loans in ${c.name}, ${c.stateName}`, description: desc, state: c.stateName, city: c.name })) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            cityWebPageJsonLd({ url, city: c.name, state: c.stateName, description: desc }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            cityLoanProductJsonLd({ url, city: c.name, state: c.stateName, description: desc }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(cityHowToJsonLd({ url, city: c.name, state: c.stateName })),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(hapTimelineHowToJsonLd(url)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            cityDatasetJsonLd({ url, city: c.name, state: c.stateName, zip: c.centroidZip }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(citySpeakableJsonLd({ url })),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            localBusinessJsonLd({ url, state: c.stateName, city: c.name }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            cityArticleJsonLd({
              url,
              headline: `Section 8 DSCR Loans in ${c.name}, ${c.stateName}`,
              description: desc,
              image: hero?.src,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbListJsonLd([
              { name: "Home", url: "/" },
              { name: "Section 8 DSCR Loans", url: `/${SLUG}` },
              { name: c.stateName, url: `/${SLUG}/${params.state}` },
              { name: c.name, url },
            ]),
          ),
        },
      ],
    };
  },
  component: CityPage,
  notFoundComponent: () => (
    <SiteShell hideBreadcrumbs>
      <Section>
        <div className="mx-auto max-w-2xl py-16 text-center">
          <h1 className="font-display text-3xl font-bold">City not found</h1>
          <p className="mt-2 text-muted-foreground">
            See our full <Link to="/where-we-lend" className="text-brand underline">list of states where we lend</Link>.
          </p>
        </div>
      </Section>
    </SiteShell>
  ),
});

function cityFaqs(city: string, state: string) {
  return [
    { q: `Do you lend on Section 8 rental properties in ${city}, ${state}?`, a: `Yes. We fund business-purpose DSCR loans on Housing Choice Voucher (Section 8) rentals in ${city} and across ${state} on 1–4 unit residential, warrantable condos, and townhomes.` },
    { q: `Can HAP contract rent be used to qualify a ${city} DSCR loan?`, a: `Yes — but understand the split. Under Section 8 the landlord collects 100% of the contract rent, yet only the HAP (government) portion is guaranteed — the tenant pays the remaining ~30% directly and that share is not government-backed. Conservative lenders underwrite to the guaranteed HAP-only figure; more aggressive programs count the full contract rent since Section 8 tenants rarely default (losing a voucher is costly). We price ${city} files both ways.` },
    { q: `What is the Payment Standard in ${city}?`, a: `Each Public Housing Agency (PHA) sets a Payment Standard, usually between 90–110% of HUD FMR. In many ${city} ZIPs the Payment Standard runs slightly above FMR, which lifts the qualifying rent used on your DSCR file.` },
    { q: `What is the minimum DSCR for Section 8 DSCR loans in ${city}?`, a: `Standard programs require a minimum DSCR of 1.00, with best pricing at 1.20+. Select sub-1.0 DSCR programs are also available with compensating factors (larger down payment, stronger FICO, or added reserves). In ${city}, Section 8 HAP rent often produces a stronger DSCR than open-market rent on the same address.` },
    { q: `What is the maximum LTV on a ${city} Section 8 DSCR loan?`, a: `Up to 80% LTV on purchases (85% by exception at 740+ FICO on an SFR) and 70–75% on cash-out refinances, subject to credit score, DSCR, reserves, and HQS inspection status.` },
    { q: `Do I have to accept vouchers to use this loan program?`, a: `No — the loan does not require voucher tenancy. But if you do accept a HAP contract, we use that rent to qualify. Many ${state} jurisdictions treat source-of-income discrimination as illegal, so check local rules before turning voucher tenants away.` },
    { q: `Does the property need to pass HQS inspection?`, a: `The property must pass HUD's Housing Quality Standards (HQS) inspection before the PHA will execute the HAP contract. This is a landlord requirement, not a loan requirement — but plan the rehab budget around it.` },
    { q: `Can I close in an LLC or entity in ${city}?`, a: `Yes. LLC, LP, S-Corp, C-Corp, and trust vesting are supported with a member/manager personal guarantee. This is standard for business-purpose Section 8 investment loans in ${state}.` },
    { q: `What credit score do I need for a Section 8 investor loan in ${city}?`, a: `Programs start at a 660 FICO, with the strongest pricing at 720+. Lower scores may still qualify with stronger DSCR, larger down payment, or additional reserves.` },
    { q: `How do property taxes affect DSCR on a Section 8 ${city} rental?`, a: `We underwrite to post-closing tax reality (not the seller's current bill) plus real insurance quotes. This is the #1 reason ${city} Section 8 DSCRs come in lower than a napkin estimate.` },
    { q: `Can I refinance a ${city} property that already has a HAP contract?`, a: `Yes. Cash-out refinances up to 75% LTV are available on seasoned ${city} Section 8 rentals with an active HAP contract. Bring the HAP contract, most recent PHA rent adjustment letter, and last HQS inspection report.` },
    { q: `Are there prepayment penalties on ${city} Section 8 DSCR loans?`, a: `Most DSCR programs carry a step-down prepay (commonly 5/4/3/2/1 or 3-year) with buy-down options. We'll price with and without prepay so you can compare.` },
    { q: `How fast can we close a Section 8 DSCR loan in ${city}?`, a: `Typical close timeline is 15–25 business days after title is opened and appraisal is ordered. Files with entity docs, HAP contract, and PHA inspection ready can close on the faster end.` },
    { q: `Do you finance BRRRR or rehab-to-Section-8 projects in ${city}?`, a: `Yes. We offer bridge/fix-and-flip loans for the acquisition and HQS-ready rehab phase in ${city}, then convert to a long-term Section 8 DSCR loan once the HAP contract is signed.` },
    { q: `Where can I look up ${city} HUD FMR and Payment Standard?`, a: `HUD publishes FMR by county and ZIP at huduser.gov. For the exact Payment Standard used on your ${city} tenant, contact the local PHA — it may differ from FMR by ±10%.` },
  ];
}

function mergedFaqs(city: string, state: string, s8?: Section8CityContext) {
  const base = cityFaqs(city, state);
  if (!s8 || s8.faqs.length === 0) return base;
  // Prepend unique per-city FAQs so they surface first in JSON-LD and UI.
  return [...s8.faqs, ...base];
}

function cityWebPageJsonLd(opts: { url: string; city: string; state: string; description: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${opts.url}#webpage`,
    url: opts.url,
    name: `Section 8 DSCR Loans in ${opts.city}, ${opts.state}`,
    description: opts.description,
    inLanguage: "en-US",
    isPartOf: { "@type": "WebSite", name: "Simply Approved Mortgages", url: "/" },
    about: { "@type": "LoanOrCredit", name: `Section 8 rental property loan in ${opts.city}, ${opts.state}` },
    mainEntity: { "@id": `${opts.url}#loan-product` },
  };
}
function cityLoanProductJsonLd(opts: { url: string; city: string; state: string; description: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    "@id": `${opts.url}#loan-product`,
    name: `Section 8 DSCR Loans in ${opts.city}, ${opts.state}`,
    url: opts.url,
    description: opts.description,
    category: "Business-purpose Section 8 / Housing Choice Voucher rental property mortgage",
    provider: brokerOrgJsonLd(),
    areaServed: [
      { "@type": "City", name: opts.city, containedInPlace: { "@type": "State", name: opts.state } },
      { "@type": "State", name: opts.state },
    ],
    feesAndCommissionsSpecification:
      "Loan amounts generally $100,000–$2,500,000; up to 80% LTV (85% by exception at 740+ FICO on an SFR); DSCR floor 0.75 with 1.00+ required for full leverage; best pricing at 1.20+; reserves 0–12 months PITIA depending on credit tier, leverage, and loan size.",
  };
}
function cityHowToJsonLd(opts: { url: string; city: string; state: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: `How to finance a Section 8 rental in ${opts.city}, ${opts.state}`,
    description: `Steps to price a business-purpose Section 8 / HAP-backed DSCR rental loan in ${opts.city}, ${opts.state}.`,
    url: opts.url,
    step: [
      { "@type": "HowToStep", name: "Send the property + HAP scenario", text: "Provide the subject address, purchase price or payoff, HAP contract rent (or PHA Payment Standard estimate), taxes, insurance, and HOA." },
      { "@type": "HowToStep", name: "Compare HAP vs. market rent", text: "We benchmark HUD FMR, PHA Payment Standard, and open-market rent so the higher qualifying figure carries the DSCR." },
      { "@type": "HowToStep", name: "Run DSCR and LTV", text: "Calculate DSCR using HAP or market rent divided by PITIA and size the loan to program LTV and reserve limits." },
      { "@type": "HowToStep", name: "Receive initial scenario review", text: "A Simply Approved Mortgages loan officer reviews the file and responds with scenario-specific pricing feedback." },
    ],
  };
}
function cityDatasetJsonLd(opts: { url: string; city: string; state: string; zip: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `HUD FMR, Payment Standard, and Section 8 rent data for ${opts.city}, ${opts.state}`,
    description: `ZIP-level HUD Fair Market Rent and Payment Standard context used on the ${opts.city}, ${opts.state} Section 8 investor guide.`,
    url: `${opts.url}#income`,
    spatialCoverage: {
      "@type": "Place",
      name: `${opts.city}, ${opts.state}`,
      address: { "@type": "PostalAddress", postalCode: opts.zip, addressCountry: "US" },
    },
    temporalCoverage: "2026",
    creator: { "@type": "Organization", name: "Simply Approved Mortgages" },
    isBasedOn: "https://www.huduser.gov/portal/dataset/fmr-api.html",
  };
}
function citySpeakableJsonLd(opts: { url: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "SpeakableSpecification",
    cssSelector: ["h1", "#tldr", "#income", "#guidelines"],
    url: opts.url,
  };
}

function CityPage() {
  const {
    city,
    fmr,
    liveVintage,
    liveMedianRent,
    liveMedianRent2BR,
    liveMedianRent3BR,
    liveUnemploymentRate,
    liveUnemploymentPeriod,
    countyInsights, marketStats, s8: s8Data } = Route.useLoaderData();
  const entity = requiresEntity(city.stateSlug);
  const state = getState(city.stateSlug);
  const s8 = s8Data ?? undefined;
  const mergedWatchouts = s8
    ? Array.from(new Set([...s8.watchouts, ...city.watchouts]))
    : city.watchouts;
  const stateCities = publishedInvestorCities.filter(
    (c) => c.stateSlug === city.stateSlug && c.slug !== city.slug,
  );
  const otherCities = (
    stateCities.length > 0
      ? stateCities
      : publishedInvestorCities.filter((c) => c.slug !== city.slug)
  ).slice(0, 8);
  const hero =
    getCityHero(city.stateSlug, city.slug, city.name, city.stateName) ??
    getProductHero(SLUG);
  const topMarkets = state?.topMarkets ?? [city.name];
  return (
    <SiteShell hideBreadcrumbs productLabel="Section 8 DSCR">
      <PageHero
        eyebrow={`${city.name}, ${city.stateName}`}
        title={`Section 8 DSCR loans in ${city.name}, ${city.stateName}`}
        subtitle={`Business-purpose DSCR financing for Housing Choice Voucher rentals in ${city.name}. Qualify with HAP contract rent, benchmarked against HUD FMR and the local PHA Payment Standard.`}
        image={hero}
      />
      <Section>
        <Breadcrumbs
          items={[
            { name: "Home", href: "/" },
            { name: "Section 8 DSCR Loans", href: `/${SLUG}` },
            { name: city.stateName, href: `/${SLUG}/${city.stateSlug}` },
            { name: city.name },
          ]}
        />
      </Section>

      <QuickAnswer
        question={`Can I get an investment property loan in ${city.name}, ${city.stateName}?`}
        answer={`Yes — Simply Approved Mortgages arranges business-purpose financing on non-owner-occupied investment property in ${city.name}, ${city.stateName}, qualified on the property's rental cash flow rather than personal tax returns. Every figure on this page is an estimate and remains subject to a complete application, credit and property review, appraisal where required, and full underwriting.`}
      />
      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#snapshot", label: "Market snapshot" },
          { href: "#income", label: "HAP vs. market rent" },
          { href: "#program", label: "Program terms" },
          { href: "#guidelines", label: "Loan guidelines" },
          { href: "#pha", label: "PHA & HQS" },
          { href: "#pha-directory", label: "PHA directory" },
          { href: "#s8-calculators", label: "HAP calculators" },
          { href: "#hap-timeline-section", label: "HAP timeline" },
          { href: "#hqs", label: "HQS checklist" },
          { href: "#fundamentals", label: "Investment fundamentals" },
          { href: "#landlord", label: "Landlord law" },
          { href: "#docs", label: "Documents" },
          { href: "#tools", label: "Fit tools" },
          { href: "#local-links", label: `${city.stateName} links` },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <Section id="tldr">
        <div className="rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6 md:p-8">
          <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-brand">
            TL;DR — Section 8 in {city.name}
          </div>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li>• Qualify with <strong>HAP contract rent</strong> — often above market</li>
            <li>• HUD FMR benchmark ZIP: <strong>{city.centroidZip}</strong></li>
            <li>• Payment Standard: <strong>{s8?.paymentStandardBand ?? "90–110% of FMR"}</strong>{s8?.usesSafmr ? " (SAFMR by ZIP)" : ""}</li>
            <li>• Min DSCR: <strong>1.00 standard · sub-1.0 available</strong> (best at 1.20+)</li>
            <li>• Max LTV: <strong>up to 80% purchase (85% by exception) / 70–75% cash-out</strong></li>
            <li>• 30-yr fixed, 5/6 & 7/6 ARM, interest-only options</li>
            <li>• Vesting: <strong>LLC / LP / S-Corp</strong>{entity ? " (required in this state)" : ""}</li>
            <li>• Property must pass <strong>HUD HQS inspection</strong></li>
            <li>• Typical close: <strong>15–25 business days</strong></li>
            <li>• Median home value: <strong>{city.medianHomeValue}</strong></li>
          </ul>
        </div>
      </Section>

      <Section id="snapshot">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <StatRow
              items={[
                { label: "Population", value: city.population },
                { label: "Median home value", value: city.medianHomeValue },
                {
                  label: liveMedianRent2BR ? "Median 2BR rent" : liveMedianRent ? "Median gross rent" : "Median 2BR rent",
                  value: liveMedianRent2BR
                    ? `$${liveMedianRent2BR.toLocaleString()}/mo`
                    : liveMedianRent
                      ? `$${liveMedianRent.toLocaleString()}/mo`
                      : city.medianRent2BR,
                },
                {
                  label: liveMedianRent3BR ? "Median 3BR rent" : "Cap rate band",
                  value: liveMedianRent3BR ? `$${liveMedianRent3BR.toLocaleString()}/mo` : city.capRateBand,
                },
              ]}
            />
            {(liveVintage || liveUnemploymentRate != null) && (
              <p className="mt-2 text-xs text-muted-foreground">
                {liveVintage && (
                  <>Population, median home value, and rent by bedroom live from U.S. Census ACS {liveVintage} (B25031). </>
                )}
                {liveUnemploymentRate != null && (
                  <>Statewide unemployment: <strong>{liveUnemploymentRate.toFixed(1)}%</strong>
                    {liveUnemploymentPeriod ? ` (${liveUnemploymentPeriod})` : ""} — U.S. Bureau of Labor Statistics LAUS, updated monthly.</>
                )}
              </p>
            )}
            <div className="prose max-w-none">
              <h2 className="font-display text-2xl font-bold text-brand-dark">Market snapshot</h2>
              <p className="mt-3 text-muted-foreground">{s8?.narrative ?? city.narrative}</p>
              {s8 && (
                <p className="mt-3 text-muted-foreground">{city.narrative}</p>
              )}
              {city.county && (
                <p className="mt-2 text-sm text-muted-foreground">
                  County: {city.county} · Centroid ZIP: {city.centroidZip}
                </p>
              )}
              <p className="mt-3 text-muted-foreground">
                Comparing options? See our{" "}
                <Link to="/section-8-loans" className="font-semibold text-brand underline underline-offset-2">
                  Section 8 investor program
                </Link>{" "}
                or the{" "}
                <Link
                  to="/section-8-loans/$state"
                  params={{ state: city.stateSlug }}
                  className="font-semibold text-brand underline underline-offset-2"
                >
                  {city.stateName} Section 8 guide
                </Link>{" "}
                for state-level rules.
              </p>
            </div>
            {s8 && (
              <div className="rounded-2xl border border-brand/20 bg-brand/5 p-5 md:p-6">
                <h3 className="font-display text-lg font-bold text-brand-dark">
                  Section 8 fit in {city.name}
                </h3>
                <dl className="mt-3 grid gap-3 text-sm md:grid-cols-3">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Primary PHA</dt>
                    <dd className="mt-1 text-foreground">{s8.primaryPha}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Payment Standard</dt>
                    <dd className="mt-1 text-foreground">
                      {s8.paymentStandardBand}
                      {s8.usesSafmr ? " (SAFMR)" : ""}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tenant demand</dt>
                    <dd className="mt-1 text-foreground">{s8.tenantDemand}</dd>
                  </div>
                </dl>
              </div>
            )}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">Strengths</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {city.strengths.map((s: string) => <li key={s}>• {s}</li>)}
                </ul>
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">Watch-outs</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {mergedWatchouts.map((s: string) => <li key={s}>• {s}</li>)}
                </ul>
              </div>
            </div>
            {entity && (
              <div className="rounded-xl border border-brand-gold/40 bg-brand-gold/5 p-5 text-sm">
                <strong className="text-brand-dark">Entity vesting required in {city.stateName}.</strong>{" "}
                Business-purpose loans in this state must vest in an LLC, LP, or similar entity. We can guide setup at pre-approval.
              </div>
            )}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">What to have ready</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• Subject property address + purchase price or payoff</li>
                  <li>• Executed HAP contract (or PHA Payment Standard estimate)</li>
                  <li>• Most recent HQS inspection report (for refi)</li>
                  <li>• LLC / entity docs (or we'll help you form one)</li>
                  <li>• Reserves (1–6 months PITIA) in a verifiable account</li>
                </ul>
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">How {city.name} typically prices</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• HAP rent above market boosts DSCR without lender adjustment</li>
                  <li>• Best pricing at DSCR 1.20+ with 25% down; full leverage needs 1.00 DSCR at up to 80% LTV, and the program floor is 0.75 DSCR at 75% LTV with a 680 FICO</li>
                  <li>• Cash-out on seasoned HAP-in-place rentals up to 75% LTV</li>
                  <li>• Portfolio blanket loans available on multi-property Section 8 books</li>
                  <li>• Close in 15–25 business days once docs are in</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <CityGuideDownload city={city} />
            <div id="lead-form" className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <h3 className="font-display text-lg font-bold text-foreground">Get Section 8 pricing for {city.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                A licensed loan officer replies with an initial scenario review once we have your credit authorization and supporting documents — usually same business day. Formal pricing and terms follow underwriting.
              </p>
              <Link
                to="/apply"
                search={{ product: "section-8" } as never}
                className="mt-4 inline-flex items-center justify-center rounded-md bg-brand-gold px-5 py-2.5 text-sm font-semibold text-primary hover:bg-brand-gold-hover"
              >
                Start My Approval
              </Link>
              <Link
                to="/rates"
                className="mt-2 inline-flex items-center justify-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-brand hover:text-brand"
              >
                See Today's Rates
              </Link>
            </div>
            <div className="rounded-xl border border-border bg-card p-5 text-xs text-muted-foreground">
              <div className="font-semibold text-foreground">Reviewed by our Section 8 desk — HAP vs. contract-rent DSCR modeled on every file</div>
              <div className="mt-1">Licensed loan officer team · NMLS #2620881</div>
              <PageDatesBadge only="reviewed" className="mt-2" />
              <div className="mt-3">
                <VerifiedStamp
                  label="HUD FMR + Payment Standard"
                  sourceUrl="https://www.huduser.gov/portal/datasets/fmr.html"
                  sourceLabel="HUD.gov"
                />
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-semibold text-brand-dark">Other {stateCities.length > 0 ? city.stateName : "investor"} cities</h3>
              <ul className="mt-3 grid grid-cols-2 gap-2 text-sm">
                {otherCities.map((c) => (
                  <li key={`${c.stateSlug}-${c.slug}`}>
                    <Link
                      to="/section-8-loans/$state/$city"
                      params={{ state: c.stateSlug, city: c.slug }}
                      className="text-brand hover:underline"
                    >
                      {c.name}, {c.stateName}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <Section id="income">
        <RentalIncomeCompareControlled
          zip={city.centroidZip}
          locationLabel={`${city.name}, ${city.stateName}`}
          medianRent2BR={city.medianRent2BR}
          medianHomeValue={city.medianHomeValue}
          airbnbAdr={city.airbnbAdr}
          airbnbOccupancy={city.airbnbOccupancy}
        />
      </Section>

      <InlineCTA
        title={`Compare HAP rent vs. market rent on a ${city.name} address`}
        body="Underwrite with Section 8 HUD FMR, PHA Payment Standard, and open-market rent side-by-side before you make an offer — then send us the scenario and a licensed loan officer will follow up with indicative numbers, subject to underwriting."
        cta="Get Your Personalized Rate Quote"
        variant="soft"
      />

      <Section id="program">
        <SectionHeader eyebrow="Program terms" title={`Section 8 investor terms in ${city.name}`} />
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Card title="Down payment & LTV" body="20–25% down on purchases (up to 80% LTV; 85% by exception at 740+ FICO on an SFR). Cash-out refinances up to 70–75% LTV once the HAP contract and ownership are seasoned six months." />
          <Card title="Qualifying rent" body="Executed HAP contract rent is used for DSCR. Landlord collects 100% of the contract rent, but only the HAP portion (~70%) is government-guaranteed — we price files on both bases (HAP-only conservative and full-contract aggressive). FMR and Payment Standard are the guardrails." />
          <Card title="Reserves" body="1–6 months of PITIA in a verifiable account depending on FICO, DSCR, and program tier. Portfolio blanket loans may require 6–12 months depending on unit count." />
          <Card title="Vesting" body={`LLC, LP, S-Corp, or C-Corp with member/manager guarantee. ${entity ? `${city.stateName} requires entity vesting on business-purpose loans.` : "Individual vesting available in this state."}`} />
          <Card title="Rate structure" body="30-year fixed, 5/6 and 7/6 ARM, and I/O options. Step-down or 3-year prepay standard with buy-down available." />
          <Card title="Closing" body={`Typical 15–25 business day close once title, HAP contract, and appraisal are in.`} />
        </div>
      </Section>

      <Section id="guidelines">
        <LoanGuidelines
          productLabel={`Section 8 · ${city.name}, ${city.stateName}`}
          rows={[
            { label: "Loan amount", value: "$100k – $3M+", why: "Sized to appraised value, program caps, and comps." },
            { label: "Max LTV", value: "Up to 80% purchase (85% by exception) / 70–75% cash-out", why: "Best pricing typically ~10% below the LTV cap." },
            { label: "Min DSCR", value: "1.00 standard · sub-1.0 available", why: `HAP rent in ${city.name} often clears 1.15–1.35 DSCR at 25% down. Standard programs floor at 1.00; select sub-1.0 DSCR programs available with compensating factors (larger down, stronger FICO, added reserves). Best pricing at 1.20+.` },
            { label: "Qualifying rent", value: "HAP contract rent (or market, higher)", why: `HUD FMR for ZIP ${city.centroidZip} and the local PHA Payment Standard are the sanity checks.` },
            { label: "Reserves", value: "1–6 months PITIA (6–12 for blanket)", why: "Tier depends on FICO, DSCR, LTV, and door count. Verified in a bank statement after close." },
            { label: "Credit", value: "660 FICO minimum (720+ best pricing)", why: "Lower scores may still qualify with stronger DSCR or larger down." },
            { label: "Terms", value: "30-yr fixed, 5/6 & 7/6 ARM, I/O options", why: "Rate/term picked to match hold period." },
            { label: "Vesting", value: entity ? "LLC / LP / S-Corp / C-Corp (entity required)" : "Individual or LLC / LP / S-Corp / C-Corp", why: "Business-purpose loans close in your entity of choice." },
            { label: "Typical close", value: "15–25 business days", why: "Faster with HAP contract, HQS inspection, and entity docs already in hand." },
          ]}
          note="Ranges are typical program guidelines — not a commitment to lend. Final terms are established in an initial scenario review after underwriting."
        />
      </Section>

      <Section id="pha">
        <SectionHeader
          eyebrow="PHA & HQS"
          title={`How the ${city.name} PHA sets rent — and what breaks the deal`}
          intro="Section 8 files live and die at the Public Housing Agency. These are the local variables to check before you underwrite the DSCR."
        />
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Card title="Local PHA Payment Standard" body={`Contact the ${city.name} / ${city.stateName} PHA for the exact Payment Standard on the ZIP. Most PHAs publish it by bedroom count and update annually.`} />
          <Card title="HUD FMR benchmark" body={`We pull HUD FMR for centroid ZIP ${city.centroidZip} as the sanity check. If HAP contract rent runs far above FMR, expect a rent reasonableness review from the PHA.`} />
          <Card title="HQS inspection" body={`Every unit must pass HUD Housing Quality Standards before the HAP contract executes. Budget for smoke/CO detectors, GFCI outlets, handrails, roof, and heat/AC compliance.`} />
          <Card title="Rent reasonableness" body="PHAs cap HAP rent at what comparable non-voucher units command in the same submarket. Bring 2–3 comparable rentals to the inspection when you're pushing the top of the range." />
          <Card title="Utility allowance" body="If the tenant pays utilities directly, the PHA subtracts a utility allowance from the total housing payment. That changes your HAP portion — model it in DSCR." />
          <Card title="Turnover & vacancy" body={`Voucher tenants typically stay longer than market-rate tenants, but PHA re-inspection at each rent adjustment can slow turnover if repairs are open.`} />
        </div>
      </Section>

      <Section id="pha-directory">
        <PhaDirectoryPanel stateSlug={city.stateSlug} stateName={city.stateName} cityName={city.name} />
      </Section>

      <Section id="voucher-demand">
        <VoucherDemandPanel
          stateAbbrev={(getState(city.stateSlug)?.abbrev ?? "").toUpperCase()}
          stateName={city.stateName}
          cityName={city.name}
        />
      </Section>

      <Section id="s8-calculators">
        <Section8Calculators
          locationLabel={`${city.name}, ${city.stateName}`}
          stateSlug={city.stateSlug}
          citySlug={city.slug}
          {...(fmr
            ? {
                defaultFmr: fmr.value,
                defaultMarketRent: Number(liveMedianRent2BR ?? city.medianRent2BR ?? fmr.value),
                fmrSource: {
                  label: fmr.areaName
                    ? `${fmr.areaName} (ZIP ${fmr.zip})`
                    : `${city.name}, ${city.stateName} (ZIP ${fmr.zip})`,
                  year: fmr.year,
                  smallArea: fmr.smallArea,
                },
              }
            : {})}
        />
      </Section>

      <Section id="sister-product">
        <SisterProductLinks
          self="section-8"
          stateSlug={city.stateSlug}
          stateName={city.stateName}
          citySlug={city.slug}
          cityName={city.name}
        />
      </Section>



      <Section id="hap-timeline-section">
        <HapTimelinePanel locationLabel={`${city.name}, ${city.stateName}`} />
      </Section>

      <Section id="case-study">
        <Section8CaseStudy location={`${city.name}, ${city.stateName}`} />
      </Section>

      <Section id="hqs">
        <HqsChecklistDownload locationLabel={`${city.name}, ${city.stateName}`} />
      </Section>

      <Section id="fundamentals">
        <InvestmentRationale
          location={`${city.name}, ${city.stateName}`}
          economy={
            city.economy ??
            `${city.name} sits inside a service-area investor market with steady voucher demand where Section 8 files should be underwritten to verified HAP rent, taxes, insurance, HOA dues, and local rental rules.`
          }
          employers={city.employers}
          transit={
            city.transit ??
            `Review neighborhood access to employment centers, transit routes, and major roads before assuming long-term voucher tenant demand in ${city.name}.`
          }
          livability={
            city.livability ??
            `${city.name} deals should be checked at the neighborhood level for vacancy, insurance, taxes, HOA restrictions, and PHA inspection history.`
          }
          demandDrivers={city.demandDrivers?.length ? city.demandDrivers : city.strengths}
        />
        <div className="mt-6">
          <DataSources locationLabel={`${city.name}, ${city.stateName}`} />
        </div>
      </Section>

      <Section id="landlord">
        <LandlordLawPanel stateSlug={city.stateSlug} stateName={city.stateName} />
      </Section>

      <Section id="docs">
        <SectionHeader eyebrow="Documents" title={`What to gather before you apply for a ${city.name} Section 8 DSCR loan`} />
        <ul className="mt-6 grid gap-2 md:grid-cols-2 text-sm text-foreground">
          <li>• Signed purchase contract (or payoff statement for refi)</li>
          <li>• Executed HAP contract (or PHA Payment Standard letter)</li>
          <li>• Most recent HQS inspection report (for refi)</li>
          <li>• Executed lease with voucher tenant (for refi)</li>
          <li>• Property insurance quote for the subject address</li>
          <li>• LLC operating agreement (or we help you form one)</li>
          <li>• Reserves statements (1–6 months PITIA depending on tier)</li>
          <li>• Government-issued ID + credit authorization</li>
        </ul>
      </Section>

      <MarketUpdateSection productSlug={SLUG} productLabel={`Section 8 · ${city.name}, ${city.stateName}`} />

      <Section>
        <ProductReportCtaBanner
          productKey="section-8"
          variant="full"
          locationLabel={`${city.name}, ${city.stateName}`}
        />
      </Section>




      <Section id="resources">
        <Section8Resources stateSlug={city.stateSlug} stateName={city.stateName} />
      </Section>

      <Section id="local-links">
        <SectionHeader
          eyebrow="Keep exploring"
          title={`More ${city.stateName} investor resources`}
          intro={`Jump into the ${city.stateName} state guide, our Section 8 program, current rates, and other ${city.stateName} markets we lend in.`}
        />
        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          <Link
            to="/section-8-loans"
            className="group rounded-xl border border-border bg-card p-5 transition hover:border-brand"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">Program hub</div>
            <h3 className="mt-2 font-display text-lg font-bold text-foreground group-hover:text-brand">
              Section 8 DSCR loan requirements →
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              HAP contract rules, LTV, DSCR formula, HQS inspection, documents, and application path.
            </p>
          </Link>
          <Link
            to="/section-8-loans/$state"
            params={{ state: city.stateSlug }}
            className="group rounded-xl border border-border bg-card p-5 transition hover:border-brand"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">State guide</div>
            <h3 className="mt-2 font-display text-lg font-bold text-foreground group-hover:text-brand">
              {city.stateName} Section 8 guide →
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Statewide lending notes, top voucher markets, PHA rules, and rental benchmarks.
            </p>
          </Link>
          <Link to="/rates" className="group rounded-xl border border-border bg-card p-5 transition hover:border-brand">
            <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">Pricing</div>
            <h3 className="mt-2 font-display text-lg font-bold text-foreground group-hover:text-brand">
              See Today's Rates →
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Review current rate context before sending the subject property for written pricing.
            </p>
          </Link>
        </div>
        {otherCities.length > 0 && (
          <div className="mt-8">
            <h3 className="font-display text-xl font-bold text-foreground">
              Top {city.stateName} markets Section 8 investors compare with {city.name}
            </h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {otherCities.map((c) => (
                <Link
                  key={`${c.stateSlug}-${c.slug}`}
                  to="/section-8-loans/$state/$city"
                  params={{ state: c.stateSlug, city: c.slug }}
                  className="rounded-lg border border-border bg-card px-4 py-3 text-sm transition hover:border-brand hover:text-brand"
                >
                  <span className="font-semibold">{c.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {c.medianRent2BR} 2BR FMR benchmark · {c.capRateBand} cap
                  </span>
                </Link>
              ))}
            </div>
            {state && (
              <p className="mt-4 text-sm text-muted-foreground">
                Other {city.stateName} markets we lend in include {topMarkets.join(", ")}.
              </p>
            )}
          </div>
        )}
      </Section>

      <Section id="faq">
        <SectionHeader
          eyebrow="FAQ"
          title={`${city.name} Section 8 DSCR loan questions`}
          intro={`Answers to the most common investor questions about Section 8 / HAP-backed DSCR loans in ${city.name}, ${city.stateName}.`}
          center
        />
        <div className="mt-8">
          <FAQ items={mergedFaqs(city.name, city.stateName, s8)} />
        </div>
      </Section>

      <ExpertInsight insight={buildCityExpertInsight(city.name, city.stateName, "section-8-loans", "Section 8 loan", ALL_STATES.find((s) => s.name === city.stateName))} />

      <CTABand
        title={`Ready to finance a ${city.name} Section 8 rental?`}
        body={`Send the address and HAP contract with your short pre-qualification form, and a licensed loan officer will follow up with an initial scenario review for ${city.name}, ${city.stateName}.`}
      />
      <LocalMarketNews
        stateName={city.stateName}
        stateAbbrev={getStateForCounty(city.stateSlug)?.abbrev ?? ""}
        cityName={city.name}
        countyName={city.county ?? null}
        stats={marketStats ?? null}
        statsScope="city"
        countyInsights={countyInsights ?? null}
      />
      <CountyInsightsPanel
        stateAbbrev={getStateForCounty(city.stateSlug)?.abbrev ?? ""}
        stateName={city.stateName}
        countyName={city.county}
        placeLabel={`${city.name}, ${city.stateName}`}
        initialData={countyInsights}
      />
      <RenterDemandPanel countyName={city.county ?? null} stateName={city.stateName} />


      <PageOutro hideInsight />
    </SiteShell>
  );
}

function Card({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="font-display text-base font-semibold text-foreground">{title}</div>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function RentalIncomeCompareControlled(
  props: Omit<React.ComponentProps<typeof RentalIncomeCompare>, "value" | "onChange">,
) {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const mode: "sfr" | "section8" | "airbnb" =
    search.income === "airbnb" ? "airbnb" : search.income === "sfr" ? "sfr" : "section8";
  return (
    <RentalIncomeCompare
      {...props}
      value={mode}
      onChange={(next) =>
        navigate({
          search: (prev: { income?: string }) => ({ ...prev, income: next }),
          replace: true,
          resetScroll: false,
        })
      }
    />
  );
}
